export const languages = ['es', 'en', 'pt', 'nl'] as const
export type Language = typeof languages[number]
export const defaultLanguage: Language = 'es'

export interface LanguageDetectionResult {
  language: Language
  source: 'localStorage' | 'querystring' | 'geolocation' | 'browser' | 'default'
}

// localStorage key for language preference
const LANGUAGE_STORAGE_KEY = 'prodoctivity_preferred_language'

/**
 * Save language preference to localStorage
 */
export function saveLanguagePreference(language: Language): void {
  if (typeof window === 'undefined') return
  
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language)
    console.log('Language preference saved:', language)
  } catch (error) {
    console.warn('Failed to save language preference:', error)
  }
}

/**
 * Get language preference from localStorage
 */
export function getLanguageFromLocalStorage(): Language | null {
  if (typeof window === 'undefined') return null
  
  try {
    const savedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY)
    
    if (savedLanguage && languages.includes(savedLanguage as Language)) {
      return savedLanguage as Language
    }
  } catch (error) {
    console.warn('Failed to get language preference from localStorage:', error)
  }
  
  return null
}

/**
 * Extract language from URL querystring
 */
export function getLanguageFromQueryString(): Language | null {
  if (typeof window === 'undefined') return null
  
  const urlParams = new URLSearchParams(window.location.search)
  const lang = urlParams.get('lang')
  
  if (lang && languages.includes(lang as Language)) {
    return lang as Language
  }
  
  return null
}

/**
 * Get language from browser settings
 */
export function getLanguageFromBrowser(): Language | null {
  if (typeof window === 'undefined') return null
  
  const browserLang = navigator.language.toLowerCase()
  
  // Check for exact matches first
  if (browserLang.startsWith('es')) return 'es'
  if (browserLang.startsWith('en')) return 'en'
  if (browserLang.startsWith('pt')) return 'pt'
  if (browserLang.startsWith('nl')) return 'nl'
  
  return null
}

/**
 * Get language from IP geolocation (simplified)
 * In a real implementation, you'd use a service like IP2Location, MaxMind, etc.
 */
export async function getLanguageFromGeolocation(): Promise<Language | null> {
  try {
    // This is a simplified example. In production, you'd use a proper geolocation service
    // For now, we'll use a basic IP-based detection service
    const response = await fetch('https://ipapi.co/json/')
    const data = await response.json()
    
    if (!data.country_code) return null
    
    const countryCode = data.country_code.toLowerCase()
    
    // Map countries to languages
    const countryLanguageMap: Record<string, Language> = {
      // Spanish-speaking countries
      'es': 'es', 'mx': 'es', 'ar': 'es', 'co': 'es', 'pe': 'es', 
      've': 'es', 'cl': 'es', 'ec': 'es', 'gt': 'es', 'cu': 'es',
      'bo': 'es', 'do': 'es', 'hn': 'es', 'py': 'es', 'sv': 'es',
      'ni': 'es', 'cr': 'es', 'pa': 'es', 'uy': 'es', 'gq': 'es',
      
      // Portuguese-speaking countries
      'br': 'pt', 'pt': 'pt', 'ao': 'pt', 'mz': 'pt', 'gw': 'pt',
      'cv': 'pt', 'st': 'pt', 'tl': 'pt',
      
      // Dutch-speaking countries
      'nl': 'nl', 'be': 'nl', 'sr': 'nl', 'aw': 'nl', 'cw': 'nl', 'sx': 'nl',
      
      // English-speaking countries (default to English for many countries)
      'us': 'en', 'gb': 'en', 'ca': 'en', 'au': 'en', 'nz': 'en',
      'ie': 'en', 'za': 'en', 'in': 'en', 'pk': 'en', 'ng': 'en',
      'ke': 'en', 'ug': 'en', 'tz': 'en', 'gh': 'en', 'zw': 'en',
    }
    
    return countryLanguageMap[countryCode] || null
  } catch (error) {
    console.warn('Geolocation detection failed:', error)
    return null
  }
}

/**
 * Detect the best language for the user
 */
export async function detectUserLanguage(): Promise<LanguageDetectionResult> {
  // Priority 1: Querystring (for explicit overrides)
  const queryLang = getLanguageFromQueryString()
  if (queryLang) {
    return { language: queryLang, source: 'querystring' }
  }
  
  // Priority 2: User's saved preference in localStorage
  const savedLang = getLanguageFromLocalStorage()
  if (savedLang) {
    return { language: savedLang, source: 'localStorage' }
  }
  
  // Priority 3: Browser language
  const browserLang = getLanguageFromBrowser()
  if (browserLang) {
    return { language: browserLang, source: 'browser' }
  }
  
  // Priority 4: Geolocation (with fallback for unusual/unreliable results)
  try {
    const geoLang = await getLanguageFromGeolocation()
    if (geoLang) {
      return { language: geoLang, source: 'geolocation' }
    }
  } catch (error) {
    console.warn('Geolocation detection failed, falling back to default')
  }
  
  // Fallback: Default language
  return { language: defaultLanguage, source: 'default' }
}

/**
 * Get current language from URL path
 */
export function getCurrentLanguageFromPath(): Language {
  if (typeof window === 'undefined') return defaultLanguage

  const pathSegments = window.location.pathname.split('/').filter(Boolean)

  // First segment should be the language when using custom domain
  const langSegment = pathSegments[0]

  if (langSegment && languages.includes(langSegment as Language)) {
    return langSegment as Language
  }

  return defaultLanguage
}

/**
 * Redirect to the appropriate language path
 */
export function redirectToLanguage(language: Language, currentPath?: string): void {
  if (typeof window === 'undefined') return

  const path = currentPath || window.location.pathname
  const search = window.location.search

  const pathSegments = path.split('/').filter(Boolean)

  let restPath = ''

  // Remove any existing language prefix
  if (pathSegments[0] && languages.includes(pathSegments[0] as Language)) {
    restPath = pathSegments.slice(1).join('/')
  } else {
    restPath = pathSegments.join('/')
  }

  // Build the new path: /language/restPath
  const newPath = `/${language}${restPath ? '/' + restPath : '/'}${search}`
  window.location.href = newPath
}
